import Skeleton from 'react-loading-skeleton';

import { Box, ChevronRight, OrderWithIncidence, Spinner } from '@/icons';
import { CustomSkeletonTheme, NextLink, Text } from '@/atoms';
import { Button } from '@/molecules';

import { ConditionalWrapper } from '@/components/hoc';
import { routes } from '@/lib/routes';
import {
  formatDateShort,
  getOrderStateColor,
  isBadOrder,
  toCurrency,
} from '@/lib/utils';

import type { FunctionComponent } from 'react';
import type { TypeCardOrderProps } from './types';

/**
 * CardOrder
 */
export const CardOrder: FunctionComponent<TypeCardOrderProps> = ({
  className = '',
  id,
  date,
  price,
  subOrders,
  status,
  closed,
  loading,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClick = () => {},
  buttonLoad,
}: TypeCardOrderProps) => (
  <CustomSkeletonTheme>
    <div
      className={`u-body flex flex-col border border-primary-100 bg-white ${className}`}
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex justify-between">
          <Text className="text-bold u-body--l flex flex-row gap-1">
            Pedido Nº{' '}
            {loading ? <Skeleton containerClassName="block w-20" /> : id}
          </Text>

          <Text className="u-body--xs capitalize">
            {loading ? (
              <Skeleton containerClassName="block w-16" />
            ) : (
              // TODO: Fix type becase date is always a string in this point
              formatDateShort(date ?? '')
            )}
          </Text>
        </div>

        <div className="flex items-end justify-between">
          <div className="u-body--s">
            {!loading && closed && <Text>{status}</Text>}

            <Text className="text-typo-tertiary">
              {loading ? (
                <Skeleton containerClassName="block w-16" />
              ) : (
                toCurrency(price)
              )}
            </Text>
          </div>

          <ConditionalWrapper
            condition={!loading}
            wrapper={(children) => (
              <NextLink
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                href={routes.micuenta.purchases.order(id)}
                {...(!closed && { passHref: true })}
              >
                {children}
              </NextLink>
            )}
          >
            {!loading && closed ? (
              <a
                aria-disabled={loading || buttonLoad}
                onClick={() => onClick(id)}
                className="aria-disabled:pointer-events-none aria-disabled:opacity-20"
              >
                {loading || buttonLoad ? (
                  <Spinner
                    width={24}
                    height={24}
                    className="animate-spin text-secondary-500 motion-reduce:hidden"
                  />
                ) : (
                  <ChevronRight
                    width={24}
                    height={24}
                    className="text-secondary-500"
                  />
                )}
              </a>
            ) : (
              <Button
                onClick={() => onClick(id)}
                as="a"
                disabled={loading || buttonLoad}
                loading={buttonLoad}
              >
                ver detalle
              </Button>
            )}
          </ConditionalWrapper>
        </div>
      </div>

      {!loading &&
        !closed &&
        subOrders?.map((subOrder, index: number) => (
          <div
            className="flex flex-col gap-2 border-t border-primary-100 bg-white p-3"
            key={index}
          >
            <div className="u-body--s flex justify-between">
              <div className="flex items-center gap-2.5">
                {subOrder?.hasIncidence ? (
                  <OrderWithIncidence width={26} height={23} />
                ) : (
                  <Box width={19} />
                )}

                <Text className="text-bold">{subOrder.seller}</Text>
              </div>

              <Text className={getOrderStateColor(subOrder.status)}>
                {subOrder.status}
              </Text>
            </div>

            <div className="u-body--xs flex justify-between">
              <Text className="text-typo-tertiary">
                {subOrder.numItems} articulos.
              </Text>
              {subOrder.deliveryDate && !isBadOrder(subOrder.status) && (
                <Text>
                  {new Date(subOrder.deliveryDate) >= new Date()
                    ? 'Llegará: '
                    : ''}
                  {formatDateShort(subOrder.deliveryDate)}
                </Text>
              )}
            </div>
          </div>
        ))}
    </div>
  </CustomSkeletonTheme>
);

CardOrder.displayName = 'CardOrder';
